import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as yup from 'yup';
import { DataContext } from '../../context/DataContext';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  IconButton,
  Divider,
  Chip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  InputAdornment,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Scale as ScaleIcon,
  Payment as PaymentIcon,
  Discount as DiscountIcon,
  Gavel as GavelIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as OrderIcon,
  Store as SupplierIcon,
  LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const SupplierTransactionForm = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { suppliers, loading, addSupplierTransaction, companyDetails } = useContext(DataContext);
  // State for master data from company details
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get metal shortcut
  const getMetalShortcut = useCallback((metalName) => {
    if (!metalName) return '';
    const metal = metalTypes.find(m =>
      m.name.toLowerCase() === metalName.toLowerCase()
    );
    return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  }, [metalTypes]);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enhanced Styled Components with Glassmorphism
  const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 20,
    overflow: 'hidden',
    backdropFilter: 'blur(20px)',
    background: alpha(theme.palette.background.paper, 0.4),
    border: `1px solid ${alpha(
      isDark ? theme.palette.divider : '#e2e8f0',
      isDark ? 0.2 : 0.3
    )}`,
    boxShadow: theme.shadows[4],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-4px)',
      background: alpha(theme.palette.background.paper, 0.5),
    },
  }));

  const SummaryCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 20,
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
    border: `1px solid ${alpha(
      isDark ? theme.palette.divider : '#e2e8f0',
      isDark ? 0.2 : 0.3
    )}`,
    boxShadow: theme.shadows[6],
    position: 'sticky',
    top: 20,
  }));

  const TransactionTypeButton = styled(Button)(({ theme, selected }) => ({
    flex: 1,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: selected
      ? `2px solid ${alpha('#8B5CF6', 0.8)}`
      : `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.5)}`,
    background: selected
      ? isDark
        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.08) 100%)'
      : 'transparent',
    color: selected ? (isDark ? '#C4B5FD' : '#8B5CF6') : (isDark ? '#94A3B8' : '#64748B'),
    fontWeight: selected ? 700 : 500,
    fontSize: '0.85rem',
    textTransform: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: selected
        ? alpha('#8B5CF6', isDark ? 0.3 : 0.2)
        : alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.1 : 0.05),
      transform: 'translateY(-1px)',
      boxShadow: selected ? `0 4px 12px ${alpha('#8B5CF6', 0.2)}` : 'none',
    },
  }));

  const BalanceBadge = styled(Chip)(({ theme, type }) => ({
    padding: '8px 12px',
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '0.85rem',
    backdropFilter: 'blur(10px)',
    background: type === 'cash'
      ? isDark
        ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(29, 78, 216, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.08) 100%)'
      : type === 'bank'
        ? isDark
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.08) 100%)'
        : isDark
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.08) 100%)',
    border: `1px solid ${type === 'cash'
      ? alpha('#3B82F6', isDark ? 0.3 : 0.2)
      : type === 'bank'
        ? alpha('#F59E0B', isDark ? 0.3 : 0.2)
        : alpha('#10B981', isDark ? 0.3 : 0.2)
      }`,
    color: type === 'cash'
      ? isDark ? '#60A5FA' : '#2563EB'
      : type === 'bank'
        ? isDark ? '#FBBF24' : '#D97706'
        : isDark ? '#34D399' : '#059669',
  }));

  const ActionButton = styled(Button)(({ theme, varianttype }) => ({
    borderRadius: 12,
    fontWeight: 600,
    textTransform: 'none',
    padding: '12px 24px',
    fontSize: '0.95rem',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...(varianttype === 'primary' && {
      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      color: '#FFFFFF',
      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
      '&:hover': {
        background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
        boxShadow: '0 6px 25px rgba(139, 92, 246, 0.4)',
        transform: 'translateY(-2px)',
      },
      '&.Mui-disabled': {
        background: alpha('#8B5CF6', 0.3),
        boxShadow: 'none',
      },
    }),
    ...(varianttype === 'secondary' && {
      background: alpha(isDark ? theme.palette.background.paper : '#FFFFFF', 0.8),
      color: isDark ? '#E2E8F0' : '#475569',
      border: `1.5px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.5)}`,
      '&:hover': {
        background: alpha(isDark ? theme.palette.background.paper : '#FFFFFF', 0.9),
        borderColor: isDark ? '#64748B' : '#CBD5E1',
        transform: 'translateY(-1px)',
      },
    }),
    ...(varianttype === 'success' && {
      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      color: '#FFFFFF',
      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
      '&:hover': {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        boxShadow: '0 6px 25px rgba(16, 185, 129, 0.4)',
        transform: 'translateY(-2px)',
      },
    }),
  }));

  // Enhanced TextField with glassmorphism
  const EnhancedTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backdropFilter: 'blur(10px)',
      backgroundColor: alpha(
        isDark ? theme.palette.background.paper : '#FFFFFF',
        isDark ? 0.3 : 0.8
      ),
      border: `1.5px solid ${alpha(
        isDark ? theme.palette.divider : '#E2E8F0',
        isDark ? 0.2 : 0.3
      )}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: alpha(
          isDark ? theme.palette.background.paper : '#FFFFFF',
          isDark ? 0.4 : 0.9
        ),
        borderColor: alpha('#8B5CF6', isDark ? 0.4 : 0.3),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(
          isDark ? theme.palette.background.paper : '#FFFFFF',
          isDark ? 0.4 : 0.9
        ),
        borderColor: '#8B5CF6',
        borderWidth: '2px',
        boxShadow: `0 0 0 3px ${alpha('#8B5CF6', 0.1)}`,
      },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? '#94A3B8' : '#64748B',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#8B5CF6',
    },
    '& .MuiOutlinedInput-input': {
      color: isDark ? '#F1F5F9' : '#1E293B',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    },
  }));

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const localDate = `${year}-${month}-${day}`;

  const initialValues = {
    supplier: '',
    date: localDate,
    items: [
      {
        type: 'piece',
        itemName: '',
        metal: 'gold',
        count: '',
        price: '',
        percent: '',
        rate: '',
        weight: '',
        wastage: '',
        labour: '',
        melting: '',
        total: '',
        fineWeight: '',
      },
    ],
    balanceType: 'cash',
    gstApplicable: false,
    discount: {
      amount: '',
      type: 'fixed',
    },
    payments: {
      cash: '',
      bank: '',
    },
    finePayments: [
      {
        weight: '',
        purity: '',
        actualFine: '',
      },
    ],
  };

  const calculateItemTotal = (item) => {
    if (item.type === 'piece') {
      const count = parseFloat(item.count) || 0;
      const price = parseFloat(item.price) || 0;
      return {
        amount: count * price,
        fine: 0
      };
    } else if (item.type === 'melting') {
      const percent = parseFloat(item.percent) || 0;
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      const amount = ((wastage + percent) / 100) * weight * rate;
      return {
        amount,
        fine: 0
      };
    } else if (item.type === 'pure') {
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const labour = parseFloat(item.labour) || 0;
      const amount = weight * (rate + labour);
      return {
        amount,
        fine: 0
      };
    } else if (item.type === 'fine') {
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      const weight = parseFloat(item.weight) || 0;
      const fine = ((melting + wastage) / 100) * weight;
      return {
        amount: 0,
        fine
      };
    }
    return {
      amount: 0,
      fine: 0
    };
  };

  // Calculate transaction totals
  const calculateTotals = (values) => {
    let totalAmount = 0;
    let totalFine = 0;

    // Calculate item totals
    const itemsWithTotals = values.items.map((item) => {
      const { amount, fine } = calculateItemTotal(item);

      totalAmount += amount;
      totalFine += fine;

      return {
        ...item,
        amount,
        fine
      };
    });

    // Calculate discount
    const discountAmount = parseFloat(values.discount.amount) || 0;
    const discountType = values.discount.type || 'fixed';
    let discountValue = 0;

    if (discountType === 'fixed') {
      discountValue = discountAmount;
    } else if (discountType === 'percentage') {
      discountValue = totalAmount * (discountAmount / 100);
    }

    const discountedAmount = Math.max(0, totalAmount - discountValue);

    let cgst = 0, sgst = 0, grandTotal = discountedAmount;
    if (values.gstApplicable) {
      cgst = totalAmount * 0.015;
      sgst = totalAmount * 0.015;
      grandTotal = totalAmount + cgst + sgst - discountValue;
    }

    let totalFinePayment = 0;
    values.finePayments.forEach((fine) => {
      const weight = parseFloat(fine.weight) || 0;
      const purity = parseFloat(fine.purity) || 0;
      const actualFine = (weight * purity) / 100;
      totalFinePayment += actualFine;
    });

    // Calculate payment done
    const cashPayment = parseFloat(values.payments.cash) || 0;
    const bankPayment = parseFloat(values.payments.bank) || 0;
    const paymentDone = cashPayment + bankPayment;

    // Calculate pending amounts
    const pendingAmount = grandTotal - paymentDone;
    const pendingFine = totalFine - totalFinePayment;

    return {
      itemsWithTotals,
      totalAmount,
      discountValue,
      cgst,
      sgst,
      grandTotal,
      totalFine,
      totalFinePayment,
      paymentDone,
      pendingAmount,
      pendingFine
    };
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsSubmitting(true);
    try {
      const totals = calculateTotals(values);

      const transactionData = {
        supplier: selectedSupplier?.id,
        date: values.date,
        balanceType: values.balanceType,
        items: totals.itemsWithTotals.map(item => ({
          ...item,
          count: parseFloat(item.count) || 0,
          price: parseFloat(item.price) || 0,
          percent: parseFloat(item.percent) || 0,
          rate: parseFloat(item.rate) || 0,
          weight: parseFloat(item.weight) || 0,
          wastage: parseFloat(item.wastage) || 0,
          labour: parseFloat(item.labour) || 0,
          melting: parseFloat(item.melting) || 0,
          amount: parseFloat(item.amount) || 0,
          fine: parseFloat(item.fine) || 0,
        })),
        gstApplicable: values.gstApplicable,
        discount: {
          amount: parseFloat(values.discount.amount) || 0,
          type: values.discount.type,
        },
        payments: {
          cash: parseFloat(values.payments.cash) || 0,
          bank: parseFloat(values.payments.bank) || 0,
        },
        finePayments: values.finePayments.map(fine => ({
          weight: parseFloat(fine.weight) || 0,
          purity: parseFloat(fine.purity) || 0,
          actualFine: (parseFloat(fine.weight) || 0) * (parseFloat(fine.purity) || 0) / 100,
        })),
        summary: {
          cgst: totals.cgst,
          sgst: totals.sgst,
          subTotal: totals.totalAmount,
          discount: totals.discountValue,
          grandTotal: totals.grandTotal,
          paymentDone: totals.paymentDone,
          fineBalance: totals.totalFine,
          finePayment: totals.totalFinePayment,
          pendingFine: totals.pendingFine,
          pendingAmount: totals.pendingAmount,
        },
      };

      await addSupplierTransaction(transactionData);
      alert('✅ Transaction saved successfully!');
      resetForm();
      setSelectedSupplier(null);
      setResetDialogOpen(false);
      setExpandedItems([0]);
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert('❌ Failed to save transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Render transaction type fields
  const renderTransactionDetails = (item, index, errors, setFieldValue, values) => {
    const itemErrors = errors?.items?.[index] || {};
    const { amount, fine } = calculateItemTotal(values.items[index]);

    const getFieldsByType = () => {
      switch (item.type) {
        case 'piece':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Field
                  name={`items.${index}.itemName`}
                  as={EnhancedTextField}
                  fullWidth
                  label="Item Name *"
                  variant="outlined"
                  size="small"
                  error={!!itemErrors.itemName}
                  helperText={itemErrors.itemName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" >
                  <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Metal *</InputLabel>
                  <Field
                    name={`items.${index}.metal`}
                    as={Select}
                    label="Metal *"
                    variant="outlined"
                    sx={{
                      width: '150px',
                      borderRadius: 12,
                      backdropFilter: 'blur(10px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#FFFFFF',
                        isDark ? 0.3 : 0.8
                      ),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(isDark ? '#2f69bb' : '#7da5da', 0.8),
                      },
                    }}
                  >
                    {metalTypes.map((metal) => (
                      <MenuItem key={metal.id} value={metal.name.toLowerCase()}>
                        {metal.name} ({metal.shortcut})
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Field
                  name={`items.${index}.count`}
                  as={EnhancedTextField}
                  fullWidth
                  label="Number of Pieces *"
                  type="text"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { min: 1 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <OrderIcon sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  error={!!itemErrors.count}
                  helperText={itemErrors.count}
                  onChange={(e) => {
                    setFieldValue(`items.${index}.count`, e.target.value);
                    // Auto-calculate total
                    const newCount = parseFloat(e.target.value) || 0;
                    const price = parseFloat(values.items[index].price) || 0;
                    setFieldValue(`items.${index}.total`, (newCount * price).toFixed(2));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Field
                  name={`items.${index}.price`}
                  as={EnhancedTextField}
                  fullWidth
                  label="Price Per Piece (₹) *"
                  type="text"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 },
                    startAdornment: (
                      <InputAdornment position="start">
                        ₹
                      </InputAdornment>
                    ),
                  }}
                  error={!!itemErrors.price}
                  helperText={itemErrors.price}
                  onChange={(e) => {
                    setFieldValue(`items.${index}.price`, e.target.value);
                    // Auto-calculate total
                    const count = parseFloat(values.items[index].count) || 0;
                    const newPrice = parseFloat(e.target.value) || 0;
                    setFieldValue(`items.${index}.total`, (count * newPrice).toFixed(2));
                  }}
                />
              </Grid>
              {/* Total Field for Piece */}
              <Grid item xs={12} sm={6} md={3}>
                <EnhancedTextField
                  fullWidth
                  label="Total Amount (₹)"
                  value={amount.toFixed(2)}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: alpha('#10B981', 0.1),
                      borderColor: alpha('#10B981', 0.3),
                    },
                    '& .MuiOutlinedInput-input': {
                      fontWeight: 600,
                      color: '#10B981',
                    }
                  }}
                />
              </Grid>
            </Grid>
          );

        case 'melting':
          return (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.itemName`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Item Name *"
                    variant="outlined"
                    size="small"
                    error={!!itemErrors.itemName}
                    helperText={itemErrors.itemName}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Metal *</InputLabel>
                    <Field
                      name={`items.${index}.metal`}
                      as={Select}
                      label="Metal *"
                      sx={{
                        borderRadius: 12,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#FFFFFF',
                          isDark ? 0.3 : 0.8
                        ),
                      }}
                    >
                      {metalTypes.map((metal) => (
                        <MenuItem key={metal.id} value={metal.name.toLowerCase()}>
                          {metal.name} ({metal.shortcut})
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.percent`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Melting (%) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!itemErrors.percent}
                    helperText={itemErrors.percent}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.percent`, e.target.value);
                      calculateMeltingTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.rate`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Metal Rate (₹) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    error={!!itemErrors.rate}
                    helperText={itemErrors.rate}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.rate`, e.target.value);
                      calculateMeltingTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.weight`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Net Weight (g) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                    error={!!itemErrors.weight}
                    helperText={itemErrors.weight}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.weight`, e.target.value);
                      calculateMeltingTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.wastage`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Wastage (%) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!itemErrors.wastage}
                    helperText={itemErrors.wastage}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.wastage`, e.target.value);
                      calculateMeltingTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                {/* Total Field for Melting */}
                <Grid item xs={12} sm={6} md={3}>
                  <EnhancedTextField
                    fullWidth
                    label="Total Amount (₹)"
                    value={amount.toFixed(2)}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: alpha('#10B981', 0.1),
                        borderColor: alpha('#10B981', 0.3),
                      },
                      '& .MuiOutlinedInput-input': {
                        fontWeight: 600,
                        color: '#10B981',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          );

        case 'pure':
          return (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.itemName`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Item Name *"
                    variant="outlined"
                    size="small"
                    error={!!itemErrors.itemName}
                    helperText={itemErrors.itemName}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Metal *</InputLabel>
                    <Field
                      name={`items.${index}.metal`}
                      as={Select}
                      label="Metal *"
                      sx={{
                        borderRadius: 12,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#FFFFFF',
                          isDark ? 0.3 : 0.8
                        ),
                      }}
                    >
                      {metalTypes.map((metal) => (
                        <MenuItem key={metal.id} value={metal.name.toLowerCase()}>
                          {metal.name} ({metal.shortcut})
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.rate`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Metal Rate (₹) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    error={!!itemErrors.rate}
                    helperText={itemErrors.rate}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.rate`, e.target.value);
                      calculatePureTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.weight`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Net Weight (g) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                    error={!!itemErrors.weight}
                    helperText={itemErrors.weight}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.weight`, e.target.value);
                      calculatePureTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.labour`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Labour (₹) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    error={!!itemErrors.labour}
                    helperText={itemErrors.labour}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.labour`, e.target.value);
                      calculatePureTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                {/* Total Field for Pure */}
                <Grid item xs={12} sm={6} md={3}>
                  <EnhancedTextField
                    fullWidth
                    label="Total Amount (₹)"
                    value={amount.toFixed(2)}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: alpha('#10B981', 0.1),
                        borderColor: alpha('#10B981', 0.3),
                      },
                      '& .MuiOutlinedInput-input': {
                        fontWeight: 600,
                        color: '#10B981',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          );

        case 'fine':
          return (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.itemName`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Item Name *"
                    variant="outlined"
                    size="small"
                    error={!!itemErrors.itemName}
                    helperText={itemErrors.itemName}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Metal *</InputLabel>
                    <Field
                      name={`items.${index}.metal`}
                      as={Select}
                      label="Metal *"
                      sx={{
                        borderRadius: 12,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#FFFFFF',
                          isDark ? 0.3 : 0.8
                        ),
                      }}
                    >
                      {metalTypes.map((metal) => (
                        <MenuItem key={metal.id} value={metal.name.toLowerCase()}>
                          {metal.name} ({metal.shortcut})
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.melting`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Melting (%) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!itemErrors.melting}
                    helperText={itemErrors.melting}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.melting`, e.target.value);
                      calculateFineTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.wastage`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Wastage (%) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!itemErrors.wastage}
                    helperText={itemErrors.wastage}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.wastage`, e.target.value);
                      calculateFineTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Field
                    name={`items.${index}.weight`}
                    as={EnhancedTextField}
                    fullWidth
                    label="Net Weight (g) *"
                    type="text"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                    error={!!itemErrors.weight}
                    helperText={itemErrors.weight}
                    onChange={(e) => {
                      setFieldValue(`items.${index}.weight`, e.target.value);
                      calculateFineTotal(index, values, setFieldValue);
                    }}
                  />
                </Grid>
                {/* Total Field for Fine (in grams) */}
                <Grid item xs={12} sm={6} md={3}>
                  <EnhancedTextField
                    fullWidth
                    label="Fine Weight (g)"
                    value={fine.toFixed(3)}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: alpha('#F59E0B', 0.1),
                        borderColor: alpha('#F59E0B', 0.3),
                      },
                      '& .MuiOutlinedInput-input': {
                        fontWeight: 600,
                        color: '#F59E0B',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          );

        default:
          return null;
      }
    };

    const calculateMeltingTotal = (index, values, setFieldValue) => {
      const item = values.items[index];
      const percent = parseFloat(item.percent) || 0;
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      const total = ((wastage + percent) / 100) * weight * rate;
      setFieldValue(`items.${index}.total`, total.toFixed(2));
    };

    const calculatePureTotal = (index, values, setFieldValue) => {
      const item = values.items[index];
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const labour = parseFloat(item.labour) || 0;
      const total = weight * (rate + labour);
      setFieldValue(`items.${index}.total`, total.toFixed(2));
    };

    const calculateFineTotal = (index, values, setFieldValue) => {
      const item = values.items[index];
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      const weight = parseFloat(item.weight) || 0;
      const fine = ((melting + wastage) / 100) * weight;
      setFieldValue(`items.${index}.fine`, fine.toFixed(3));
    };

    const getSummaryText = () => {
      if (item.type === 'fine') {
        return `Fine: ${fine.toFixed(3)}g`;
      } else {
        return `Amount: ₹${amount.toFixed(2)}`;
      }
    };

    const handleTypeChange = (type, setFieldValue) => {
      setFieldValue(`items.${index}.type`, type);
      const resetFields = {
        piece: ['percent', 'rate', 'weight', 'wastage', 'labour', 'melting'],
        melting: ['count', 'price', 'labour', 'melting'],
        pure: ['count', 'price', 'percent', 'wastage', 'melting'],
        fine: ['count', 'price', 'rate', 'percent', 'labour']
      };
      resetFields[type]?.forEach(field => {
        setFieldValue(`items.${index}.${field}`, '');
      });
      // Reset total when type changes
      setFieldValue(`items.${index}.total`, '');
      setFieldValue(`items.${index}.fineWeight`, '');
    };

    return (
      <Accordion
        expanded={expandedItems.includes(index)}
        onChange={() => {
          if (expandedItems.includes(index)) {
            setExpandedItems(expandedItems.filter(i => i !== index));
          } else {
            setExpandedItems([...expandedItems, index]);
          }
        }}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          background: alpha(isDark ? '#1E293B' : '#FFFFFF', isDark ? 0.3 : 0.8),
          border: `1px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.3)}`,
          boxShadow: theme.shadows[2],
          mb: 2,
          '&:before': { display: 'none' },
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: alpha('#8B5CF6', 0.4),
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: isDark ? '#94A3B8' : '#64748B' }} />}
          sx={{
            background: alpha(isDark ? '#1E293B' : '#F8FAFC', isDark ? 0.4 : 0.6),
            borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.2)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: alpha('#8B5CF6', 0.1),
              border: `1.5px solid ${alpha('#8B5CF6', 0.3)}`,
            }}>
              <InventoryIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} color={isDark ? '#F1F5F9' : '#1E293B'}>
                Item #{index + 1} - {item.itemName || 'Unnamed Item'}
              </Typography>
              <Typography variant="caption" color={isDark ? '#94A3B8' : '#64748B'}>
                {item.metal ? `${item.metal.charAt(0).toUpperCase() + item.metal.slice(1)} (${getMetalShortcut(item.metal)})` : item.metal} • {item.type === 'piece' ? 'On Piece' :
                  item.type === 'melting' ? 'On Melting' :
                    item.type === 'pure' ? 'On Pure' : 'By Fine'} • {getSummaryText()}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom color={isDark ? '#CBD5E1' : '#475569'} fontWeight={500}>
              Transaction Type
            </Typography>
            <Grid container spacing={1}>
              {['piece', 'melting', 'pure', 'fine'].map((type) => (
                <Grid item xs={3} key={type}>
                  <TransactionTypeButton
                    selected={item.type === type}
                    onClick={() => handleTypeChange(type, setFieldValue)}
                    fullWidth
                  >
                    {type === 'piece' ? 'On Piece' :
                      type === 'melting' ? 'On Melting' :
                        type === 'pure' ? 'On Pure' : 'By Fine'}
                  </TransactionTypeButton>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Divider sx={{ my: 3, borderColor: alpha(isDark ? '#475569' : '#E2E8F0', 0.3) }} />
          {getFieldsByType()}
        </AccordionDetails>
      </Accordion>
    );
  };

  const validationSchema = yup.object().shape({
    supplier: yup.string().required("Supplier is required"),
  });

  return (
    <Box sx={{
      minHeight: '100vh',
      py: 4,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      },
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        open={loading || isSubmitting}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha(isDark ? '#0F172A' : '#FFFFFF', 0.8),
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4, borderRadius: 3, backdropFilter: 'blur(20px)' }}>
          <CircularProgress
            size={60}
            sx={{
              color: isDark ? '#60A5FA' : '#10B981',
              mb: 2
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDark ? '#E2E8F0' : '#1E293B',
            }}
          >
            {isSubmitting ? 'Saving Transaction...' : 'Loading...'}
          </Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Card */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.35)',
              }}>
                <ReceiptIcon sx={{ color: '#FFFFFF', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{
                  fontWeight: 800,
                  background: isDark
                    ? 'linear-gradient(90deg, #60A5FA 0%, #3B82F6 100%)'
                    : 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}>
                  Supplier Transaction
                </Typography>
                <Typography sx={{
                  color: isDark ? '#CBD5E1' : '#64748B',
                  fontSize: '1rem',
                  fontWeight: 400,
                }}>
                  Create new supplier purchases and transactions
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, handleChange, setFieldValue, isSubmitting, errors, touched, resetForm }) => {
            const totals = calculateTotals(values);

            return (
              <Form>
                <Grid container spacing={1}>
                  {/* Left Column - Main Form */}
                  <Grid item xs={12} lg={8} sx={{ width: '100%' }}>
                    {/* Supplier Selection */}
                    <StyledCard>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha('#3B82F6', 0.1),
                            border: `1.5px solid ${alpha('#3B82F6', 0.3)}`,
                          }}>
                            <SearchIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
                          </Box>
                          <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: isDark ? '#F1F5F9' : '#1E293B',
                          }}>
                            Supplier Information
                          </Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <Autocomplete
                              freeSolo
                              options={suppliers}
                              getOptionLabel={(option) => option.name || ''}
                              value={selectedSupplier || ""}
                              onChange={(event, newValue) => {
                                if (newValue && typeof newValue === 'object') {
                                  handleSupplierSelect(newValue);
                                  setFieldValue('supplier', newValue.name);
                                }
                              }}
                              onInputChange={(event, newInputValue) => {
                                setFieldValue('supplier', newInputValue);
                              }}
                              renderInput={(params) => (
                                <EnhancedTextField
                                  {...params}
                                  label="Search Supplier *"
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    width: '400px',
                                  }}
                                  error={!!errors.supplier && touched.supplier}
                                  helperText={errors.supplier && touched.supplier && errors.supplier}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SupplierIcon sx={{ color: isDark ? '#94A3B8' : '#64748B' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props} style={{ padding: '8px 16px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{
                                      bgcolor: alpha('#8B5CF6', 0.1),
                                      color: '#8B5CF6',
                                      fontWeight: 600,
                                      border: `1.5px solid ${alpha('#8B5CF6', 0.3)}`,
                                    }}>
                                      {option.name.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                                        {option.name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                                        Balance: ₹{(parseFloat(option.balanceValueCash || 0) + parseFloat(option.balanceValueBank || 0)).toFixed(2)} |
                                        Fine: {parseFloat(option.balanceFine || 0).toFixed(2)}g
                                      </Typography>
                                    </Box>
                                  </Box>
                                </li>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Field
                              name="date"
                              as={EnhancedTextField}
                              fullWidth
                              label="Transaction Date *"
                              type="date"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.date && touched.date}
                              helperText={errors.date && touched.date && errors.date}
                            />
                          </Grid>
                        </Grid>

                        {/* Supplier Balance Display */}
                        {selectedSupplier && (
                          <Paper sx={{
                            p: 2,
                            mt: 3,
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            background: alpha(isDark ? '#1E293B' : '#F8FAFC', isDark ? 0.4 : 0.8),
                            border: `1px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.3)}`,
                          }}>
                            <Typography variant="subtitle2" gutterBottom color={isDark ? '#CBD5E1' : '#475569'}>
                              Current Supplier Balance
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <BalanceBadge
                                type="cash"
                                label={`Cash: ₹${parseFloat(selectedSupplier.balanceValueCash || 0).toFixed(2)}`}
                              />
                              <BalanceBadge
                                type="bank"
                                label={`Bank: ₹${parseFloat(selectedSupplier.balanceValueBank || 0).toFixed(2)}`}
                              />
                              <BalanceBadge
                                type="fine"
                                label={`Fine: ${parseFloat(selectedSupplier.balanceFine || 0).toFixed(2)}g`}
                              />
                            </Box>
                          </Paper>
                        )}
                      </CardContent>
                    </StyledCard>

                    {/* Items Section */}
                    <StyledCard sx={{ mt: 3 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: alpha('#10B981', 0.1),
                              border: `1.5px solid ${alpha('#10B981', 0.3)}`,
                            }}>
                              <InventoryIcon sx={{ color: '#10B981', fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" sx={{
                              fontWeight: 700,
                              color: isDark ? '#F1F5F9' : '#1E293B',
                            }}>
                              Transaction Items
                            </Typography>
                          </Box>
                          <ActionButton
                            varianttype="success"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setFieldValue('items', [
                                ...values.items,
                                { ...initialValues.items[0] },
                              ]);
                              setExpandedItems([...expandedItems, values.items.length]);
                            }}
                          >
                            Add Item
                          </ActionButton>
                        </Box>

                        <FieldArray name="items">
                          {({ remove }) => (
                            <Box>
                              {values.items.map((item, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                  {renderTransactionDetails(item, index, errors, setFieldValue, values)}
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button
                                      startIcon={<RemoveIcon />}
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={() => {
                                        if (values.items.length > 1) {
                                          remove(index);
                                          setExpandedItems(expandedItems.filter(i => i !== index));
                                        } else {
                                          alert('You need to have at least one item in the transaction.');
                                        }
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        backdropFilter: 'blur(10px)',
                                        background: alpha('#EF4444', 0.1),
                                        border: `1px solid ${alpha('#EF4444', 0.2)}`,
                                        color: '#EF4444',
                                        fontWeight: 600,
                                        '&:hover': {
                                          background: alpha('#EF4444', 0.2),
                                          borderColor: alpha('#EF4444', 0.4),
                                        },
                                      }}
                                    >
                                      Remove Item
                                    </Button>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </CardContent>
                    </StyledCard>

                    {/* GST and Discount Section */}
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <StyledCard>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: alpha('#F59E0B', 0.1),
                                border: `1.5px solid ${alpha('#F59E0B', 0.3)}`,
                              }}>
                                <GavelIcon sx={{ color: '#F59E0B', fontSize: 18 }} />
                              </Box>
                              <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: isDark ? '#F1F5F9' : '#1E293B',
                              }}>
                                Tax Settings
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Field name="gstApplicable">
                                  {({ field, form }) => (
                                    <Checkbox
                                      {...field}
                                      checked={Boolean(field.value)}
                                      sx={{
                                        color: '#8B5CF6',
                                        '&.Mui-checked': { color: '#8B5CF6' },
                                      }}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        form.setFieldValue("gstApplicable", checked);
                                        form.setFieldValue("balanceType", checked ? "bank" : "cash");
                                      }}
                                    />
                                  )}
                                </Field>
                              }
                              label={
                                <Typography variant="body2" sx={{ color: isDark ? '#CBD5E1' : '#475569' }}>
                                  GST Applicable (CGST 1.5% + SGST 1.5%)
                                </Typography>
                              }
                            />
                          </CardContent>
                        </StyledCard>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <StyledCard>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: alpha('#EC4899', 0.1),
                                border: `1.5px solid ${alpha('#EC4899', 0.3)}`,
                              }}>
                                <DiscountIcon sx={{ color: '#EC4899', fontSize: 18 }} />
                              </Box>
                              <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: isDark ? '#F1F5F9' : '#1E293B',
                              }}>
                                Discount
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={8}>
                                <Field
                                  name="discount.amount"
                                  as={EnhancedTextField}
                                  fullWidth
                                  label="Discount Amount"
                                  type="text"
                                  variant="outlined"
                                  size="small"
                                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Type</InputLabel>
                                  <Field
                                    name="discount.type"
                                    as={Select}
                                    label="Type"
                                    sx={{
                                      borderRadius: 12,
                                      backdropFilter: 'blur(10px)',
                                      backgroundColor: alpha(
                                        isDark ? theme.palette.background.paper : '#FFFFFF',
                                        isDark ? 0.3 : 0.8
                                      ),
                                    }}
                                  >
                                    <MenuItem value="fixed">Fixed</MenuItem>
                                    <MenuItem value="percentage">Percentage</MenuItem>
                                  </Field>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    </Grid>

                    {/* Payment Methods */}
                    <StyledCard sx={{ mt: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha('#8B5CF6', 0.1),
                            border: `1.5px solid ${alpha('#8B5CF6', 0.3)}`,
                          }}>
                            <PaymentIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
                          </Box>
                          <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: isDark ? '#F1F5F9' : '#1E293B',
                          }}>
                            Payment Methods
                          </Typography>
                        </Box>
                        <Grid item xs={12} md={6} sx={{ mb: 3 }}>
                          <FormControl fullWidth size="small">
                            <Field name="balanceType">
                              {({ field }) => (
                                <FormControl fullWidth size="small">
                                  <InputLabel sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                                    Amount Add to Balance Type?
                                  </InputLabel>
                                  <Select
                                    {...field}
                                    label="Amount Add to Balance Type?"
                                    value={field.value ?? "cash"}
                                    sx={{
                                      borderRadius: 12,
                                      backdropFilter: 'blur(10px)',
                                      backgroundColor: alpha(
                                        isDark ? theme.palette.background.paper : '#FFFFFF',
                                        isDark ? 0.3 : 0.8
                                      ),
                                    }}
                                  >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="bank">Bank Transfer</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="payments.cash"
                              as={EnhancedTextField}
                              fullWidth
                              label="Cash Payment (₹)"
                              type="text"
                              variant="outlined"
                              size="small"
                              InputProps={{
                                inputProps: { min: 0, step: 0.01 },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    ₹
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="payments.bank"
                              as={EnhancedTextField}
                              fullWidth
                              label="Bank Transfer (₹)"
                              type="text"
                              variant="outlined"
                              size="small"
                              InputProps={{
                                inputProps: { min: 0, step: 0.01 },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    ₹
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </StyledCard>

                    {/* Fine Payments */}
                    <StyledCard sx={{ mt: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: alpha('#F59E0B', 0.1),
                              border: `1.5px solid ${alpha('#F59E0B', 0.3)}`,
                            }}>
                              <ScaleIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" sx={{
                              fontWeight: 700,
                              color: isDark ? '#F1F5F9' : '#1E293B',
                            }}>
                              Fine Payments
                            </Typography>
                          </Box>
                          <ActionButton
                            varianttype="success"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setFieldValue('finePayments', [
                                ...values.finePayments,
                                { weight: '', purity: '', actualFine: '' },
                              ])
                            }
                          >
                            Add Fine Payment
                          </ActionButton>
                        </Box>

                        <FieldArray name="finePayments">
                          {({ remove }) => (
                            <Box>
                              {values.finePayments.map((fine, index) => (
                                <Paper key={index} sx={{
                                  p: 3,
                                  mb: 2,
                                  borderRadius: 3,
                                  backdropFilter: 'blur(10px)',
                                  background: alpha(isDark ? '#1E293B' : '#F8FAFC', isDark ? 0.4 : 0.8),
                                  border: `1px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.3)}`,
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600} color={isDark ? '#F1F5F9' : '#1E293B'}>
                                      Fine Payment #{index + 1}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        if (values.finePayments.length > 1) {
                                          remove(index);
                                        } else {
                                          alert('You need to have at least one fine payment.');
                                        }
                                      }}
                                      sx={{
                                        backdropFilter: 'blur(10px)',
                                        background: alpha('#EF4444', 0.1),
                                        border: `1px solid ${alpha('#EF4444', 0.2)}`,
                                        color: '#EF4444',
                                        '&:hover': {
                                          background: alpha('#EF4444', 0.2),
                                          borderColor: alpha('#EF4444', 0.4),
                                        },
                                      }}
                                    >
                                      <RemoveIcon />
                                    </IconButton>
                                  </Box>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <Field
                                        name={`finePayments.${index}.weight`}
                                        as={EnhancedTextField}
                                        fullWidth
                                        label="Fine Weight (grams)"
                                        type="text"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                          inputProps: { min: 0, step: 0.01 },
                                          endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Field
                                        name={`finePayments.${index}.purity`}
                                        as={EnhancedTextField}
                                        fullWidth
                                        label="Fine Purity (%)"
                                        type="text"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                          inputProps: { min: 0, max: 100, step: 0.01 },
                                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <EnhancedTextField
                                        fullWidth
                                        label="Actual Fine (grams)"
                                        value={((parseFloat(fine.weight) || 0) * (parseFloat(fine.purity) || 0) / 100).toFixed(3)}
                                        InputProps={{ readOnly: true }}
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>
                                </Paper>
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </CardContent>
                    </StyledCard>
                  </Grid>

                  {/* Right Column - Summary & Actions */}
                  <Grid item xs={12} lg={4} sx={{ m: 'auto', width: '100%', }}>
                    {/* Summary Card */}
                    <SummaryCard>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                          boxShadow: '0 8px 20px rgba(139, 92, 246, 0.3)',
                        }}>
                          <ReceiptIcon sx={{ color: '#FFFFFF', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight={800} color={isDark ? '#F1F5F9' : '#1E293B'}>
                            Transaction Summary
                          </Typography>
                          <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                            {values.items.length} items • {values.finePayments.length} fine payments
                          </Typography>
                        </Box>
                      </Box>

                      <Stack spacing={2} sx={{ mb: 4 }}>
                        <Box sx={{
                          p: 2,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          background: alpha(isDark ? '#1E293B' : '#F8FAFC', isDark ? 0.4 : 0.6),
                          border: `1px solid ${alpha(isDark ? '#475569' : '#E2E8F0', 0.3)}`,
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>Subtotal:</Typography>
                            <Typography variant="body1" fontWeight={600} color={isDark ? '#F1F5F9' : '#1E293B'}>
                              ₹{totals.totalAmount.toFixed(2)}
                            </Typography>
                          </Box>

                          {totals.totalFine > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>
                                Total Fine Weight:
                              </Typography>
                              <Typography variant="body1" color="#F59E0B" fontWeight={600}>
                                {totals.totalFine.toFixed(3)}g
                              </Typography>
                            </Box>
                          )}

                          {values.gstApplicable && (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>CGST (1.5%):</Typography>
                                <Typography variant="body1" color="#3B82F6" fontWeight={600}>
                                  ₹{totals.cgst.toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>SGST (1.5%):</Typography>
                                <Typography variant="body1" color="#3B82F6" fontWeight={600}>
                                  ₹{totals.sgst.toFixed(2)}
                                </Typography>
                              </Box>
                            </>
                          )}

                          {totals.discountValue > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>Discount:</Typography>
                              <Typography variant="body1" color="#EC4899" fontWeight={600}>
                                -₹{totals.discountValue.toFixed(2)}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ my: 2, borderColor: alpha(isDark ? '#475569' : '#E2E8F0', 0.3) }} />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body1" fontWeight={700} color={isDark ? '#F1F5F9' : '#1E293B'}>
                              Grand Total:
                            </Typography>
                            <Typography variant="h5" color="#8B5CF6" fontWeight={800}>
                              ₹{totals.grandTotal.toFixed(2)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>Payment Done:</Typography>
                            <Typography variant="body1" color="#10B981" fontWeight={600}>
                              ₹{totals.paymentDone.toFixed(2)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>Fine Payment:</Typography>
                            <Typography variant="body1" color="#F59E0B" fontWeight={600}>
                              {totals.totalFinePayment.toFixed(2)}g
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 2, borderColor: alpha(isDark ? '#475569' : '#E2E8F0', 0.3) }} />

                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            background: totals.pendingAmount > 0
                              ? alpha('#FEE2E2', isDark ? 0.2 : 0.1)
                              : alpha('#DCFCE7', isDark ? 0.2 : 0.1),
                            border: `1px solid ${totals.pendingAmount > 0
                              ? alpha('#EF4444', 0.3)
                              : alpha('#10B981', 0.3)
                              }`,
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1" fontWeight={700} color={totals.pendingAmount > 0 ? '#EF4444' : '#10B981'}>
                                {totals.pendingAmount > 0 ? 'Pending Amount:' : 'Fully Paid:'}
                              </Typography>
                              <Typography variant="h6" fontWeight={800} color={totals.pendingAmount > 0 ? '#EF4444' : '#10B981'}>
                                ₹{Math.abs(totals.pendingAmount).toFixed(2)}
                              </Typography>
                            </Box>
                            {totals.pendingFine > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2" color={isDark ? '#CBD5E1' : '#475569'}>Pending Fine:</Typography>
                                <Typography variant="body1" color="#F59E0B" fontWeight={600}>
                                  {totals.pendingFine.toFixed(2)}g
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Stack>

                      {/* Action Buttons */}
                      <Stack spacing={2}>
                        <ActionButton
                          type="submit"
                          varianttype="primary"
                          startIcon={<SaveIcon />}
                          fullWidth
                          disabled={isSubmitting || loading}
                        >
                          {isSubmitting ? 'Saving...' : 'Save Transaction'}
                        </ActionButton>

                        <ActionButton
                          type="button"
                          varianttype="secondary"
                          startIcon={<RefreshIcon />}
                          fullWidth
                          onClick={() => setResetDialogOpen(true)}
                        >
                          Reset Form
                        </ActionButton>
                      </Stack>
                    </SummaryCard>

                    {/* Quick Stats Card */}
                    <StyledCard sx={{ mt: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <TrendingUpIcon sx={{ color: '#8B5CF6', fontSize: 24 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                            Quick Stats
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              textAlign: 'center',
                              backdropFilter: 'blur(10px)',
                              background: alpha('#8B5CF6', 0.1),
                              border: `1.5px solid ${alpha('#8B5CF6', 0.3)}`,
                            }}>
                              <Typography variant="caption" display="block" color={isDark ? '#C4B5FD' : '#8B5CF6'} fontWeight={500}>
                                Total Items
                              </Typography>
                              <Typography variant="h4" fontWeight={800} color={isDark ? '#F1F5F9' : '#1E293B'}>
                                {values.items.length}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              textAlign: 'center',
                              backdropFilter: 'blur(10px)',
                              background: alpha('#10B981', 0.1),
                              border: `1.5px solid ${alpha('#10B981', 0.3)}`,
                            }}>
                              <Typography variant="caption" display="block" color={isDark ? '#34D399' : '#059669'} fontWeight={500}>
                                Fine Payments
                              </Typography>
                              <Typography variant="h4" fontWeight={800} color={isDark ? '#F1F5F9' : '#1E293B'}>
                                {values.finePayments.length}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </Grid>

                {/* Reset Confirmation Dialog */}
                <Dialog
                  open={resetDialogOpen}
                  onClose={() => setResetDialogOpen(false)}
                  PaperProps={{
                    sx: {
                      borderRadius: 3,
                      backdropFilter: 'blur(20px)',
                      background: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    }
                  }}
                >
                  <DialogTitle sx={{ fontWeight: 700, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                    Reset Form Confirmation
                  </DialogTitle>
                  <DialogContent>
                    <Typography sx={{ color: isDark ? '#CBD5E1' : '#475569' }}>
                      Are you sure you want to reset the form? All unsaved data will be lost.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setResetDialogOpen(false)}
                      sx={{
                        color: isDark ? '#94A3B8' : '#64748B',
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        resetForm();
                        setFieldValue('supplier', '');
                        setSelectedSupplier(null);
                        setResetDialogOpen(false);
                        setExpandedItems([0]);
                      }}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                          boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                        },
                      }}
                    >
                      Reset Form
                    </Button>
                  </DialogActions>
                </Dialog>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </Box>
  );
};

export default SupplierTransactionForm;